/** Shared by the callable and admin UI. No Firebase or browser dependencies. */
export type InvitationState =
  'not_sent' | 'pending' | 'sending' | 'retry' | 'sent' | 'uncertain' | 'cancelled'

export interface InvitationEvidence {
  status?: string
  invitationDeliveryStatus?: string
  invitationEmailStatus?: string
  confirmedAt?: unknown
  participantInvitedAt?: unknown
  participantOfferStatus?: string
  participantAcceptedAt?: unknown
  participantDeclinedAt?: unknown
  participantWithdrawnAt?: unknown
}

export function invitationState(submission: InvitationEvidence): InvitationState {
  const explicit = submission.invitationDeliveryStatus
  if (explicit) {
    switch (explicit) {
      case 'pending':
      case 'sending':
      case 'retry':
      case 'sent':
      case 'uncertain':
      case 'cancelled':
        return explicit
      default:
        return 'uncertain' // Unknown persisted states must never enable sending.
    }
  }
  // Package confirmedAt predates sending; its email field is the delivery evidence.
  if (submission.invitationEmailStatus === 'sent') return 'sent'
  if (submission.invitationEmailStatus === 'failed') return 'retry'
  if (submission.invitationEmailStatus === 'pending') return 'pending'
  if (submission.invitationEmailStatus) return 'uncertain'
  if (
    submission.participantInvitedAt ||
    ['invited', 'accepted_unpaid', 'paid', 'declined', 'withdrawn', 'payment_forfeited'].includes(
      submission.participantOfferStatus ?? ''
    )
  )
    return 'uncertain'
  // Compatibility for standard invitations issued before the durable mail queue.
  if (submission.confirmedAt) return 'sent'
  if (
    submission.participantAcceptedAt ||
    submission.participantDeclinedAt ||
    submission.participantWithdrawnAt
  )
    return 'uncertain'
  return 'not_sent'
}

export function isSelectedParticipant(submission: InvitationEvidence): boolean {
  return (
    submission.status === 'accepted' &&
    !submission.participantDeclinedAt &&
    !submission.participantWithdrawnAt &&
    !['declined', 'withdrawn', 'payment_forfeited'].includes(
      submission.participantOfferStatus ?? ''
    )
  )
}

export function selectionIsLocked(submission: InvitationEvidence): boolean {
  return (
    invitationState(submission) !== 'not_sent' ||
    !!submission.participantAcceptedAt ||
    !!submission.participantDeclinedAt ||
    !!submission.participantWithdrawnAt
  )
}

export const INVITATION_LABEL: Record<InvitationState, string> = {
  not_sent: 'Not sent',
  pending: 'Queued',
  sending: 'Sending',
  retry: 'Automatic retry pending',
  sent: 'Email sent',
  uncertain: 'Needs delivery review',
  cancelled: 'Cancelled',
}

export function selectionWorkflow(
  production: { status?: string; isArchived?: boolean },
  submissions: InvitationEvidence[]
) {
  const selected = submissions.filter(isSelectedParticipant)
  // Email history survives a later decline or withdrawal. Selection controls
  // eligibility for new mail, never whether a past send still counts.
  const recipients = submissions.filter(
    (s) => isSelectedParticipant(s) || invitationState(s) !== 'not_sent'
  )
  const counts: Record<InvitationState, number> = {
    not_sent: 0,
    pending: 0,
    sending: 0,
    retry: 0,
    sent: 0,
    uncertain: 0,
    cancelled: 0,
  }
  for (const submission of recipients) counts[invitationState(submission)]++
  const inactive = !!production.isArchived || production.status === 'completed'
  const processing = counts.pending + counts.sending + counts.retry
  const attention = counts.uncertain + counts.cancelled
  const state = inactive
    ? 'closed'
    : attention
      ? 'attention'
      : processing
        ? 'processing'
        : counts.not_sent
          ? 'ready'
          : recipients.length
            ? 'sent'
            : 'empty'
  return {
    state,
    selectedCount: selected.length,
    recipientCount: recipients.length,
    counts,
    processing,
    attention,
    canSend: !inactive && counts.not_sent > 0,
    newInvitationCount: counts.not_sent,
  }
}
